import { useEffect, useState } from "react";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { Plus, Trash2, X, Edit2, ScanFace, Camera, Upload, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import PasswordInput from "@/components/PasswordInput";

export default function Users() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // user object being edited, or null for create
  const [faceModalUser, setFaceModalUser] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "staff" });
  const [busy, setBusy] = useState(false);

  async function load() {
    try { const { data } = await api.get("/users"); setItems(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }
  useEffect(() => { load(); }, []);

  function openCreate() {
    setEditing(null);
    setForm({ name: "", email: "", password: "", role: "staff" });
    setOpen(true);
  }
  function openEdit(u) {
    setEditing(u);
    setForm({ name: u.name || "", email: u.email || "", password: "", role: u.role || "staff" });
    setOpen(true);
  }

  async function save() {
    if (!form.name || !form.email) { toast.error("Name and email are required"); return; }
    if (!editing && !form.password) { toast.error("Password is required for new users"); return; }
    setBusy(true);
    try {
      if (editing) {
        // Only send fields that changed / are provided
        const payload = { name: form.name, email: form.email, role: form.role };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/users", form);
        toast.success("User created");
      }
      setOpen(false);
      setEditing(null);
      setForm({ name: "", email: "", password: "", role: "staff" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  }

  async function del(id) {
    if (!window.confirm("Delete this user?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }

  return (
    <div className="p-4 sm:p-6 lg:p-10" data-testid="users-page">
      <header className="flex items-end justify-between mb-8">
        <div>
          <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mb-2">/ TEAM · ACCESS CONTROL</div>
          <h1 className="section-title text-5xl lg:text-6xl">Users.</h1>
          <p className="text-zinc-600 mt-3 text-sm max-w-xl">Add teammates, change roles, and reset passwords in one place.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setBulkOpen(true)} className="brut-btn-outline px-4 py-2.5 inline-flex items-center gap-2" data-testid="bulk-enroll-btn">
            <Upload size={16} /> Bulk Enroll Faces
          </button>
          <button onClick={openCreate} className="brut-btn px-4 py-2.5 inline-flex items-center gap-2" data-testid="add-user-btn">
            <Plus size={16} /> New User
          </button>
        </div>
      </header>

      <div className="brut-card overflow-x-auto max-w-4xl">
        <table className="brut-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Face</th>
              <th>Created</th>
              <th className="w-40">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`}>
                <td className="font-display font-bold">{u.name}</td>
                <td className="font-mono-ds text-xs break-all">{u.email}</td>
                <td><span className={`brut-badge ${u.role === "admin" ? "alert" : ""}`}>{u.role}</span></td>
                <td>
                  {u.face_enrolled_photo ? (
                    <img src={fileUrl(u.face_enrolled_photo)} alt="face" className="w-10 h-10 object-cover border-2 border-black" title={`Enrolled ${u.face_enrolled_at?.slice(0, 10)}`} data-testid={`face-thumb-${u.id}`} />
                  ) : (
                    <span className="font-mono-ds text-[10px] text-zinc-400 uppercase">not set</span>
                  )}
                </td>
                <td className="font-mono-ds text-xs">{u.created_at?.slice(0, 10)}</td>
                <td>
                  <div className="flex gap-1.5">
                    <button
                      onClick={() => setFaceModalUser(u)}
                      className={`p-1.5 border-2 border-black ${u.face_enrolled_photo ? "bg-[#22A06B] text-white hover:bg-[#1a8055]" : "hover:bg-zinc-100"}`}
                      title={u.face_enrolled_photo ? "Re-enroll face" : "Enroll face"}
                      data-testid={`enroll-face-${u.id}`}
                    >
                      <ScanFace size={14} />
                    </button>
                    <button
                      onClick={() => openEdit(u)}
                      className="p-1.5 border-2 border-black hover:bg-zinc-100"
                      title="Edit"
                      data-testid={`edit-user-${u.id}`}
                    >
                      <Edit2 size={14} />
                    </button>
                    {u.id !== user?.id ? (
                      <button
                        onClick={() => del(u.id)}
                        className="p-1.5 border-2 border-black hover:bg-[#FF3333] hover:text-white"
                        title="Delete"
                        data-testid={`delete-user-${u.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    ) : (
                      <span className="brut-badge">YOU</span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={6} className="text-center py-8 text-zinc-500 font-mono-ds text-xs uppercase">No users</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {faceModalUser && (
        <FaceEnrollModal user={faceModalUser} onClose={() => setFaceModalUser(null)} onSaved={() => { setFaceModalUser(null); load(); }} />
      )}

      {bulkOpen && (
        <BulkFaceEnrollModal users={items} onClose={() => setBulkOpen(false)} onDone={() => { setBulkOpen(false); load(); }} />
      )}

      {open && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="user-form-modal">
          <div className="brut-card w-full max-w-md bg-white">
            <div className="flex items-center justify-between p-5 border-b-2 border-black">
              <div>
                <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">{editing ? "EDIT" : "NEW"}</div>
                <h2 className="font-display font-black text-2xl tracking-tight">{editing ? "Edit User" : "New User"}</h2>
              </div>
              <button onClick={() => setOpen(false)} className="p-2 hover:bg-zinc-100" data-testid="close-user-form">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <Field label="Name">
                <input className="brut-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" />
              </Field>
              <Field label="Email">
                <input type="email" className="brut-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" />
              </Field>
              <Field label={editing ? "New Password (leave blank to keep current)" : "Password"}>
                <PasswordInput
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  testid="user-password-input"
                  placeholder={editing ? "Leave blank to keep current" : ""}
                />
              </Field>
              <Field label="Role">
                <select className="brut-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-role-select">
                  <option value="staff">Staff</option>
                  <option value="admin">Admin</option>
                </select>
                {editing && editing.id === user?.id && (
                  <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mt-1">
                    Note: You cannot demote yourself from admin.
                  </div>
                )}
              </Field>
            </div>
            <div className="border-t-2 border-black p-5 flex justify-end gap-3">
              <button onClick={() => setOpen(false)} className="brut-btn-outline px-5 py-2.5" data-testid="cancel-user-btn">Cancel</button>
              <button onClick={save} disabled={busy} className="brut-btn px-5 py-2.5" data-testid="save-user-btn">
                {busy ? "Saving…" : editing ? "Save Changes" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="font-mono-ds text-[11px] tracking-widest uppercase mb-1.5">{label}</div>
      {children}
    </label>
  );
}

function FaceEnrollModal({ user, onClose, onSaved }) {
  const [stream, setStream] = useState(null);
  const [snap, setSnap] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [videoEl, setVideoEl] = useState(null);
  useEffect(() => {
    if (!videoEl) return;
    let s;
    (async () => {
      try {
        s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: 640, height: 640 }, audio: false });
        setStream(s);
        videoEl.srcObject = s;
        await videoEl.play();
      } catch (e) { setErr(e.message || "Camera unavailable"); }
    })();
    return () => { if (s) s.getTracks().forEach((t) => t.stop()); };
  }, [videoEl]);

  function capture() {
    if (!videoEl) return;
    const c = document.createElement("canvas");
    c.width = videoEl.videoWidth || 640; c.height = videoEl.videoHeight || 480;
    c.getContext("2d").drawImage(videoEl, 0, 0, c.width, c.height);
    c.toBlob((blob) => setSnap(blob), "image/jpeg", 0.9);
  }

  async function save() {
    if (!snap) { toast.error("Take a photo first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", snap, `face-${user.id}.jpg`);
      await api.post(`/users/${user.id}/face`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Face enrolled for ${user.name}`);
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  }

  async function clearEnrollment() {
    if (!window.confirm(`Remove enrolled face for ${user.name}? They won't be able to punch until re-enrolled.`)) return;
    try {
      await api.delete(`/users/${user.id}/face`);
      toast.success("Enrollment cleared");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="face-enroll-modal">
      <div className="brut-card bg-white w-full max-w-lg">
        <div className="p-4 border-b-2 border-black flex items-center justify-between">
          <div>
            <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">FACE ENROLLMENT</div>
            <div className="font-display font-black text-xl">{user.name}</div>
          </div>
          <button onClick={onClose} className="p-1.5 border-2 border-black hover:bg-zinc-100" data-testid="close-face-modal"><X size={14} /></button>
        </div>
        <div className="p-4">
          {err && <div className="brut-card-flat bg-[#FFF0F0] p-3 mb-3 text-sm text-[#B10000]">{err}</div>}
          {user.face_enrolled_photo && !snap && (
            <div className="mb-3">
              <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mb-1">Currently enrolled</div>
              <img src={fileUrl(user.face_enrolled_photo)} alt="current" className="w-32 h-32 object-cover border-2 border-black" />
            </div>
          )}
          <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mb-1">
            {snap ? "New photo — confirm below" : user.face_enrolled_photo ? "Take a new photo to replace" : "Position face in the frame"}
          </div>
          {!snap ? (
            <video ref={setVideoEl} playsInline muted className="w-full aspect-square object-cover border-2 border-black bg-black" data-testid="face-video" />
          ) : (
            <img alt="preview" src={URL.createObjectURL(snap)} className="w-full aspect-square object-cover border-2 border-black" data-testid="face-preview" />
          )}
          <div className="brut-card-flat bg-[#FDF6EE] p-2 mt-3 text-[11px] text-zinc-700">
            <b>Tips for a good enrollment:</b> face directly at the camera · good even lighting · no sunglasses or mask · fill about 60% of the frame.
          </div>
        </div>
        <div className="p-4 border-t-2 border-black flex flex-wrap justify-end gap-2">
          {user.face_enrolled_photo && !snap && (
            <button onClick={clearEnrollment} className="brut-btn-outline px-4 py-2 text-[#FF3333]" data-testid="clear-face-btn">Clear</button>
          )}
          <button onClick={onClose} className="brut-btn-outline px-4 py-2">Cancel</button>
          {!snap ? (
            <button onClick={capture} disabled={!!err} className="brut-btn px-5 py-2 inline-flex items-center gap-2" data-testid="capture-face"><Camera size={14} /> Capture</button>
          ) : (
            <>
              <button onClick={() => setSnap(null)} className="brut-btn-outline px-4 py-2" data-testid="retake-face">Retake</button>
              <button onClick={save} disabled={busy} className="brut-btn px-5 py-2" data-testid="save-face">{busy ? "Saving…" : "Save face"}</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


function BulkFaceEnrollModal({ users, onClose, onDone }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);

  function pickMatch(filename) {
    if (!filename || !users?.length) return null;
    const stem = filename.replace(/\.[^.]+$/, "").toLowerCase();
    const key = stem.replace(/[_\-.]/g, " ").trim();
    const compact = key.replace(/\s+/g, "");
    const byId = users.find((u) => u.id?.toLowerCase() === stem);
    if (byId) return byId;
    const byEmail = users.find((u) => (u.email || "").split("@")[0].toLowerCase() === key);
    if (byEmail) return byEmail;
    const byName = users.find((u) => (u.name || "").toLowerCase().trim() === key
      || (u.name || "").toLowerCase().replace(/\s+/g, "") === compact);
    if (byName) return byName;
    const words = key.split(/\s+/).filter((w) => w.length > 1);
    if (words.length) {
      return users.find((u) => {
        const n = (u.name || "").toLowerCase();
        return words.every((w) => n.includes(w));
      }) || null;
    }
    return null;
  }

  function onPick(e) {
    const list = Array.from(e.target.files || []);
    if (list.length > 50) { toast.error("Max 50 files per batch"); return; }
    setFiles(list.map((f) => ({ file: f, name: f.name, size: f.size, match: pickMatch(f.name) })));
    setReport(null);
  }

  async function submit() {
    if (!files.length) { toast.error("Pick at least one photo"); return; }
    setBusy(true);
    setReport(null);
    try {
      const fd = new FormData();
      files.forEach((r) => fd.append("files", r.file, r.name));
      const { data } = await api.post("/users/bulk-enroll-face", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReport(data);
      if (data.ok) toast.success(`${data.ok} enrolled · ${data.failed} skipped`);
      else toast.error(`No enrollments succeeded — see report`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="bulk-enroll-modal">
      <div className="brut-card bg-white w-full max-w-2xl p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="brut-badge">BULK FACE ENROLL</div>
            <h2 className="font-display font-black text-2xl mt-2 leading-tight">Upload many photos in one shot</h2>
            <p className="text-sm text-zinc-600 mt-1 max-w-md">
              Rename each photo to the employee's <b>name</b> or <b>email prefix</b> (e.g. <code>ravi_kumar.jpg</code> or <code>ravi@…</code> → <code>ravi.jpg</code>). We match automatically.
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 border-2 border-black" aria-label="Close" data-testid="close-bulk-modal"><X size={14} /></button>
        </div>

        {!report && (
          <>
            <label className="block brut-card-flat p-6 border-dashed cursor-pointer hover:bg-[#FDF6EE] text-center" data-testid="bulk-file-label">
              <Upload size={22} className="mx-auto mb-2 text-[#7A3E1F]" />
              <div className="font-display font-black text-base">Choose photos</div>
              <div className="text-xs text-zinc-500 mt-1">JPG / PNG / WEBP · max 10MB each · up to 50 at once</div>
              <input type="file" accept="image/*" multiple onChange={onPick} className="hidden" data-testid="bulk-file-input" />
            </label>

            {files.length > 0 && (
              <div className="mt-4">
                <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mb-2">
                  {files.length} photo{files.length === 1 ? "" : "s"} · {files.filter((f) => f.match).length} auto-matched
                </div>
                <div className="brut-card-flat p-0 max-h-64 overflow-y-auto">
                  {files.map((r, i) => (
                    <div key={i} className={`flex items-center justify-between px-3 py-2 border-b border-zinc-200 text-sm ${r.match ? "" : "bg-[#FDECE0]"}`}>
                      <div className="font-mono-ds text-xs truncate max-w-[220px]" title={r.name}>{r.name}</div>
                      <div className="text-xs">
                        {r.match ? (
                          <span className="text-[#22A06B] font-bold">→ {r.match.name || r.match.email}</span>
                        ) : (
                          <span className="text-[#B34A00] font-bold">No user matched</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {report && (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div className="brut-card-flat p-3 text-center">
                <div className="font-display font-black text-3xl text-[#22A06B]">{report.ok}</div>
                <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">Enrolled</div>
              </div>
              <div className="brut-card-flat p-3 text-center">
                <div className="font-display font-black text-3xl text-[#B34A00]">{report.failed}</div>
                <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">Skipped</div>
              </div>
              <div className="brut-card-flat p-3 text-center">
                <div className="font-display font-black text-3xl">{report.total}</div>
                <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">Total</div>
              </div>
            </div>
            <div className="brut-card-flat p-0 max-h-64 overflow-y-auto" data-testid="bulk-report">
              {report.results.map((r, i) => (
                <div key={i} className={`flex items-center justify-between px-3 py-2 border-b border-zinc-200 text-sm ${r.ok ? "" : "bg-[#FDECE0]"}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    {r.ok ? <CheckCircle2 size={14} className="text-[#22A06B] shrink-0" /> : <XCircle size={14} className="text-[#B34A00] shrink-0" />}
                    <div className="font-mono-ds text-xs truncate" title={r.filename}>{r.filename}</div>
                  </div>
                  <div className="text-xs text-right ml-2 max-w-[240px]">
                    {r.user_name && <div className="font-bold text-[#2F343A] truncate">{r.user_name}</div>}
                    <div className={`text-[11px] ${r.ok ? "text-[#22A06B]" : "text-[#B34A00]"} truncate`}>{r.reason}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 mt-5 pt-4 border-t-2 border-black">
          {!report && (
            <>
              <button onClick={onClose} className="brut-btn-outline px-4 py-2">Cancel</button>
              <button onClick={submit} disabled={busy || !files.length} className="brut-btn px-5 py-2 inline-flex items-center gap-2" data-testid="submit-bulk-enroll">
                <ScanFace size={14} /> {busy ? `Enrolling ${files.length}…` : `Enroll ${files.length || ""} photo${files.length === 1 ? "" : "s"}`}
              </button>
            </>
          )}
          {report && (
            <button onClick={onDone} className="brut-btn px-5 py-2">Done</button>
          )}
        </div>
      </div>
    </div>
  );
}
